import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "umi";
import cx from 'classnames';
import CardBox from "@/pages/dashboard/components/CardBox";
import BarChart from "@/pages/dashboard/components/Chart/components/BarChart";
import MiniTable from "@/pages/dashboard/components/MiniTable";
import MetricCardList, { getCountryImg } from "@/pages/benchmark/components/MetricCardList";
import { Bubble } from "tea-component";
import { LANGUAGE_EN } from "@home/_config/config";
import { getLanguage } from "@home/utils/common";
import { COLOR_ARR } from "@/pages/dashboard/_config/constant";
import { getBaseTooltip } from "@/pages/dashboard/components/EchartsTooltip/utils";
import { xAxisHighLight } from "@/pages/dashboard/components/Chart/_config/plug";
import { handleDownload } from "@/pages/dashboard/utils/utils";
import { axisLabelFormatter, downValueFormatter, getMetricFormatRule, metricValueFormatter } from "@/pages/benchmark/utils/utils";
import dataFormat from "@home/utils/dbDataFormat/dataFormat";
import { showYAxisExtension } from "./_utils";
import * as echarts from 'echarts';
import './index.scss';

interface IMetricDetailChartProp {
  className?: string;
  style?: string;
  data: any;
  dataKey: string;
  metric: string;
  selectorList: any;
  selectMap?: any;
  showLabel?: boolean;
  filterError: boolean;
  downloadTileEn?: string;
  downloadTileCn?: string;
  gameNumEn?: string;
  gameNumCn?: string;
  gameNumFormatter?: string;
  hideSubTitle?: boolean;
  axisLabelFontSize?: string;
  isTransverse?: boolean;
  defaultActive?: string;
  tableOption?: any[];
  tooltipColumns?: any[];
}

// ！！！配置一个以上图表需要传入不同className，用于区分dom元素，注册echarts事件
const MetricDetailChart = (props: IMetricDetailChartProp) => {
  const {
    className,
    style,
    data,
    dataKey,
    metric,
    selectorList = {},
    selectMap = {
      country: "country_list",
      platform: "platform_list",
      genre: "genre_subgenre_list",
    },
    showLabel = true,
    filterError,
    downloadTileEn = "Explore_Benchmark_",
    downloadTileCn = "基准探索_行业基准_",
    gameNumEn = "Games",
    gameNumCn = "游戏",
    gameNumFormatter = "int",
    hideSubTitle = false,
    axisLabelFontSize = 12,
    isTransverse,
    defaultActive,
    tableOption,
    tooltipColumns,
  } = props || {};

  const { activeTab, activeRadio, metricConfig: allMetricConfig } = data || {};
  const { data: metricData, errorMsg, loading } = data?.[dataKey] || {};
  const { locale, formatMessage } = useIntl(); // 中英切换方法
  const isEn = locale === LANGUAGE_EN;
  const selectorData = selectorList?.[selectMap?.[activeTab?.value] || `${activeTab?.value}_list`];
  const [active, setActive] = useState(defaultActive || "10");
  const [chartType, setChartType] = useState("barChart");
  // 图表配置处理
  const {
    xAxisData, // x轴数据
    barSeries, // 柱状图数据
    table, // 表格数据
    metricConfig, // 指标配置
    title, // 标题
    dimensionText, // 维度名称
    radioText, // radio选项名称
    gameNumText, // 游戏数量名称
    gameSubGenresText, // 游戏副品类名称
    isNodata, // 是否无数据
    metricsCardList, // 头部卡片区域数据
    downloadTile, // 下载表格标题
    options, // 横向柱状图选项栏数据
    rich, // 国家code富文本映射
    richMap, // 国家name跟code映射关系（echarts富文本只识别英文字符）
  } = useMemo(() => {
    // 指标配置
    const metricConfig = allMetricConfig?.[metric] || {};
    const { nameEn, nameCn, metric_cn: metricCN, metric_en: metricEN } = metricConfig || {};
    // 标题名称设置
    const dimensionText = getLanguage(isEn, activeTab?.text?.textEn, activeTab?.text?.textCn) || "";
    const radioText = getLanguage(isEn, activeRadio?.textEn, activeRadio?.textCn) || "";
    const metricText = getLanguage(isEn, nameEn || metricEN, nameCn || metricCN) || metric;
    const gameNumText = getLanguage(isEn, gameNumEn, gameNumCn) || "";
    const gameSubGenresText = getLanguage(isEn, "Sub Genre", "副品类");
    const title = metricText;
    const downloadTile = `${isEn ? downloadTileEn : downloadTileCn}${metricText}`;
    // 图表设置
    let isNodata = true;
    const metricsCardList: any = [];
    const xAxisData: any = [];
    const seriesData: any = [];
    const tableData: any = [];
    const downloadData: any = [];
    // 富文本映射
    const rich: any = {};
    const richMap: any = {};
    const option = tableOption || [
      {
        header: dimensionText,
        key: "name"
      },
      ...(activeTab?.value === 'genre' ? [{
        header: gameSubGenresText,
        key: "gameSubGenres",
        render: (record: any) => {
          const gameSubGenres = record?.gameSubGenres || [];
          return gameSubGenres?.length > 3 ? gameSubGenres?.length : gameSubGenres?.join("/");
        }
      }] : []),
      {
        header: metricText,
        key: "value",
        render: (record: any) => metricValueFormatter(record?.value, metricConfig)
      },
      {
        header: gameNumText,
        key: "gameNum",
        render: (record: any) => dataFormat({ value: record?.gameNum, formatRule: gameNumFormatter })
      }
    ];
    // 副品类
    const subGenreMap: any = {};
    selectorList?.genre_subgenre_list?.forEach((item: any) => {
      item?.list?.forEach((v: any) => {
        subGenreMap[v?.code] = getLanguage(isEn, v?.name_en, v?.name_ch);
      });
    });
    metricData?.forEach((item: any, index: number) => {
      const { code, metrics } = item || {};
      const value = metrics?.[metric]?.[activeRadio?.value] || "";
      const gameNum = metrics?.[metric]?.game_contributors || "";
      const gameSubGenres = (metrics?.[metric]?.game_sub_genres || [])?.map((item: string | number) => subGenreMap?.[item] || item);
      if (value) {
        isNodata = false;
      }
      const selectorItem = selectorData?.find((v: { code: string; }) => v?.code === code);
      const name = getLanguage(isEn, selectorItem?.name_en, selectorItem?.name_ch) || code;
      const iconCode = activeTab?.value === 'country' ? code : null;
      xAxisData?.push(name);
      tableData?.push({ name, value, gameNum, gameSubGenres, iconCode });
      downloadData?.push({ name, value: downValueFormatter(value, metricConfig), gameNum, gameSubGenres: gameSubGenres?.length > 3 ? gameSubGenres?.length : gameSubGenres?.join("/") });
      metricsCardList?.push({ name, value: metricValueFormatter(value, metricConfig), color: COLOR_ARR?.[index], iconCode, gameNum, gameSubGenres });
      // 分国家柱状图颜色相同
      const seriesItem: any = { axisPoint: name, value, gameNum, gameSubGenres, itemStyle: { color: activeTab?.value === 'country' ? COLOR_ARR?.[0] : COLOR_ARR?.[index] || "" } };
      seriesData?.push(seriesItem);
      if (activeTab?.value === 'country') {
        rich[code] = {
          fontSize: 16,
          lineHeight: 16,
          width: 24,
          height: 16,
          backgroundColor: {
            image: getCountryImg(code)
          },
        };
        richMap[name] = code;
      }
    });
    // 横向柱状图选项栏
    const options = [
      { value: "10", len: [0, 10], text: "Top 10", data: tableData?.slice(0, 10) || [] },
      { value: "20", len: [10, 20], text: "Top 11-20", data: tableData?.slice(10, 20) || [] },
      { value: "30", len: [20, 30], text: "Top 21-30", data: tableData?.slice(20, 30) || [] },
      { value: "40", len: [30, 40], text: "Top 31-40", data: tableData?.slice(30, 40) || [] },
      { value: "50", len: [40, 50], text: "Top 41-50", data: tableData?.slice(40, 50) || [] },
    ]?.filter(v => metricData?.length > v?.len?.[0]);
    const activeOption = options?.find(v => v?.value === active);
    const len: any = activeOption?.len || [0, 10];
    // 选择单个国家数据或者没找到选中项，重置为top10选项
    if (xAxisData?.length <= 10 || !activeOption) {
      setActive("10");
    }
    const barSeries: any = [{
      name: dimensionText,
      type: "bar",
      label: {
        show: showLabel,
        color: "#262832",
        fontSize: 12,
        fontWeight: 600,
        position: 'top',
        formatter: (params: { value: string; }) => metricValueFormatter(params?.value, metricConfig)
      },
      // 横向柱状图数据截取
      data: isTransverse ? seriesData?.slice(len?.[0], len?.[1]) : seriesData
    }];
    const table = {
      option,
      data: tableData,
      downloadData,
    };
    return {
      // 横向柱状图数据截取
      xAxisData: isTransverse ? xAxisData?.slice(len?.[0], len?.[1]) : xAxisData,
      barSeries,
      table,
      metricConfig,
      title,
      dimensionText,
      radioText,
      gameNumText,
      gameSubGenresText,
      isNodata,
      metricsCardList,
      downloadTile,
      options: xAxisData?.length <= 10 ? options?.slice(0, 1) : options,
      rich,
      richMap,
    };
  }, [allMetricConfig, metric, isEn, activeTab?.text?.textEn, activeTab?.text?.textCn, activeTab?.value, activeRadio?.textEn, activeRadio?.textCn, activeRadio?.value, gameNumEn, gameNumCn, downloadTileEn, downloadTileCn, tableOption, selectorList?.genre_subgenre_list, metricData, showLabel, isTransverse, gameNumFormatter, selectorData, active]);

  const renderList = [{
    type: 'barChart',
    svgType: 'column-chart',
    content: formatMessage({ id: 'common_bar_chart' }),
    component: <>
      <BarChart
        height={isTransverse ? 523 : 340}
        legendData={[]}
        isShowLegend={false}
        xAxisData={xAxisData || []}
        series={xAxisData?.length > 0 ? barSeries : []}
        formatterFunc={(params: any) => getBaseTooltip({
          params,
          options: {
            hasShare: false,
            hasSum: false,
            columns: tooltipColumns || [
              {
                header: dimensionText,
                key: "name",
                showIcon: true,
                render: (_value, row, _column, _icon) => {
                  const name = row?.orgData?.name || "";
                  const iconDom = <span
                    className={`dbd-echarts-tooltip-container-icon circular`}
                    style={{ background: row?.color }}
                  ></span>;
                  return <>{iconDom}{name}</>;
                }
              },
              ...(activeTab?.value === 'genre' ? [{
                header: gameSubGenresText,
                key: "gameSubGenres",
                align: 'right',
                render: (_value: any, row: { orgData: { data: { gameSubGenres: never[]; }; }; }, _column: any, _icon: any) => {
                  const gameSubGenres = row?.orgData?.data?.gameSubGenres || [];
                  return gameSubGenres?.length > 3 ? gameSubGenres?.length : gameSubGenres?.join("/");
                }
              }] : []),
              {
                header: radioText,
                key: "value",
                align: 'right',
                dataFormatRule: getMetricFormatRule(metricConfig)
              },
              {
                header: gameNumText,
                key: "gameNum",
                align: 'right',
                render: (_value, row, _column, _icon) => dataFormat({ value: (row?.orgData?.data?.gameNum || 0), formatRule: gameNumFormatter })
              }
            ]
          }
        })}
        setOtherConfigFunc={(opt: any) => {
          // 设置展示横向坐标轴的维度
          if (isTransverse) {
            // 横向坐标轴柱状图
            opt.xAxis = {
              type: 'value',
              axisLabel: {
                color: '#838697',
                formatter: (value: number) => axisLabelFormatter(value, metricConfig)
              },
              splitLine: {
                show: true,
                lineStyle: {
                  type: 'dashed',
                  color: '#ddd',
                },
              },
              axisLine: {
                show: true,
                lineStyle: {
                  color: '#D8DAE4',
                },
              },
              axisTick: { show: true },
            };
            opt.yAxis = {
              type: 'category',
              data: xAxisData,
              inverse: true,
              triggerEvent: true,
              axisLine: { show: false },
              axisTick: { show: false },
              axisLabel: {
                rich: {
                  text: {
                    verticalAlign: 'middle',
                    fontSize: 14,
                    height: 16,
                    lineHeight: 16,
                    padding: [4, 0, 0, 0],
                  },
                  ...rich
                },
                color: "#262832",
                interval: 0,
                fontSize: 14,
                height: 16,
                lineHeight: 16,
                width: 170,
                overflow: "truncate",
                margin: 180,
                align: 'left',
                formatter: (name: string) => {
                  const richCode = richMap?.[name];
                  if (richCode) {
                    return `{${richCode}|} {text| ${name}}`;
                  }
                  return name;
                }
              },
            };
            opt.grid = {
              top: 10,
              left: -170,
              right: 40,
              bottom: 10,
              containLabel: true
            };
            if (opt?.series?.[0]?.label) {
              opt.series[0].barCategoryGap = 20;
              opt.series[0].label = {
                ...opt.series[0].label,
                position: 'right',
              };
            }
          } else {
            // 高亮
            opt = xAxisHighLight(opt, { fontSize: axisLabelFontSize });
            opt.grid.bottom = 10;
            // y轴格式化
            if (opt?.yAxis?.axisLabel?.formatter) {
              opt.yAxis.axisLabel.formatter = (value: number) => axisLabelFormatter(value, metricConfig);
            }
            if (opt?.xAxis?.[0]?.axisLabel?.fontSize) {
              // 产品优化需求：x轴名称都展示
              opt.xAxis[0].axisLabel.interval = 0;
              opt.xAxis[0].axisLabel.fontSize = axisLabelFontSize;
            }
          }
          return opt;
        }}
      />
      {
        isTransverse && <div className="dbb-metric-chart-footer">
          {
            options?.map((item) => {
              const { value, text, data = [] } = item || {};
              return <Bubble
                key={value}
                overlayClassName={"dbb-metric-chart-bubble"}
                content={data?.length > 0 && <table>
                  <thead>
                    <tr className="dbb-metric-chart-bubble-title">
                      <th align="left">
                        {dimensionText}
                      </th>
                      <th className="dbb-metric-chart-bubble-title-right" align="right">
                        {title}
                      </th>
                      <th className="dbb-metric-chart-bubble-title-right" align="right">
                        {gameNumText}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      data?.map((v: any) => {
                        const { iconCode, name, value, gameNum } = v || {};
                        return <tr className="dbb-metric-chart-bubble-item" key={name}>
                          <td className="dbb-metric-chart-bubble-item-left">{iconCode && <img src={getCountryImg(iconCode)} />}{name}</td>
                          <td className="dbb-metric-chart-bubble-item-right">{metricValueFormatter(value, metricConfig)}</td>
                          <td className="dbb-metric-chart-bubble-item-right">{dataFormat({ value: gameNum, formatRule: gameNumFormatter })}</td>
                        </tr>;
                      })
                    }
                  </tbody>
                </table>
                }
              >
                <div
                  className={cx(
                    { "dbb-metric-chart-footer-item": true },
                    { "active": active === value }
                  )}
                  onClick={() => setActive(value)}
                >
                  {text}
                </div>
              </Bubble>;
            })
          }
        </div >
      }
    </>
  },
  {
    type: 'sheet',
    svgType: 'sheet',
    content: formatMessage({ id: 'common_table' }),
    component: <MiniTable
      height={isTransverse ? 554 : 340}
      loading={false}
      tableOption={table?.option}
      tableList={table?.data}
    />
  }];

  // 给echarts实例y轴增加hover事件展示浮窗
  useEffect(() => {
    try {
      const dom = document.querySelector(`.${className || 'register-table'} .chart-box div`);
      // 没有获取dom或者非横向柱状图不需要注册事件
      if (!dom || !isTransverse) return;
      const chart = echarts.getInstanceByDom(dom);
      // 给横向柱状图右侧留多一个刻度空白
      try {
        if (chart) {
          const options: any = chart.getOption();
          // 获取y轴刻度区间
          const scope = chart.getModel().getComponent('xAxis').axis.scale._extent;
          // 获取y轴刻度间隔/轴距
          const interval = chart.getModel().getComponent('xAxis').axis.scale._interval;
          if (options?.xAxis?.[0]) {
            // 设置分割间隔（用于解决echarts用max再次适配生成x轴刻度）
            options.xAxis[0].interval = interval;
            // 设置最大值
            options.xAxis[0].max = scope?.[1] + interval;
            // 关闭动画
            options.animation = false;
            // 重新设置图表
            chart?.setOption(options);
          }
        }
      } catch (error) {
        console.log(error, '给横向柱状图右侧留多一个刻度空白');
      }
      const destroyYAxisExtension = showYAxisExtension(chart);
      // 销毁事件
      return () => {
        destroyYAxisExtension();
      };
    } catch (error) {
      console.log(error, '给echarts实例y轴增加hover事件展示浮窗');
    }
  }, [className, isTransverse, barSeries, chartType]);

  return <CardBox
    className={cx('register-table', className)}
    style={style}
    titleCom={title}
    subTitleCom={
      !loading && !hideSubTitle && !isTransverse && <MetricCardList
        className={'dbb-explore-metric-chart-subtitle'}
        list={metricsCardList}
        showBubble={activeTab?.value === 'genre'}
        tooltipFormat={(params: any) => getBaseTooltip({
          params: [params],
          options: {
            hasShare: false,
            hasSum: false,
            columns: tooltipColumns || [
              {
                header: dimensionText,
                key: "name",
                showIcon: true,
                render: (_value, row, _column, _icon) => {
                  const name = row?.orgData?.name || "";
                  const iconDom = <span
                    className={`dbd-echarts-tooltip-container-icon circular`}
                    style={{ background: row?.color }}
                  ></span>;
                  return <>{iconDom}{name}</>;
                }
              },
              ...(activeTab?.value === 'genre' ? [{
                header: gameSubGenresText,
                key: "gameSubGenres",
                align: 'right',
                render: (_value: any, row: any, _column: any, _icon: any) => {
                  const gameSubGenres = row?.orgData?.gameSubGenres || [];
                  return gameSubGenres?.length > 3 ? gameSubGenres?.length : gameSubGenres?.join("/");
                }
              }] : []),
              {
                header: radioText,
                key: "value",
                align: 'right',
              },
              {
                header: gameNumText,
                key: "gameNum",
                align: 'right',
                render: (_value, row, _column, _icon) => dataFormat({ value: (row?.orgData?.gameNum || 0), formatRule: gameNumFormatter })
              }
            ]
          }
        })}
      />
    }
    onChangeChartType={(tab) => {
      setChartType(tab);
    }}
    bordered={false}
    height={isTransverse ? 554 : 414}
    renderList={renderList}
    downloadClick={() => handleDownload(table, downloadTile)}
    isLoading={loading}
    isError={errorMsg || filterError}
    isNodata={isNodata}
  />;
};
export default MetricDetailChart;